import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Skeleton from '@mui/material/Skeleton';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { getWatchDetails, completeWatch, WatchDetails, WatchQuestion } from '../api/watch';

// Extend window for YouTube IFrame API
declare global {
    interface Window {
        YT: {
            Player: new (elementId: string, opts: object) => YTPlayer;
            PlayerState: { ENDED: number; PLAYING: number; PAUSED: number; BUFFERING: number };
        };
        onYouTubeIframeAPIReady: () => void;
    }
}

interface YTPlayer {
    destroy(): void;
    getDuration(): number;
    getCurrentTime(): number;
    playVideo(): void;
    pauseVideo(): void;
}

function extractVideoId(url: string): string | null {
    const match = url.match(
        /(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    );
    return match?.[1] ?? null;
}

type PageState = 'loading' | 'error' | 'already-earned' | 'watching' | 'quiz' | 'submitting' | 'success' | 'quiz-failed';

export default function WatchPage() {
    const { id } = useParams<{ id: string }>();
    const [pageState, setPageState] = useState<PageState>('loading');
    const [details, setDetails] = useState<WatchDetails | null>(null);
    const [errorMsg, setErrorMsg] = useState('');
    const [videoProgress, setVideoProgress] = useState(0);
    const [watchStart, setWatchStart] = useState<number | null>(null);

    // Quiz state
    const [answers, setAnswers] = useState<Record<string, string>>({});
    const [quizError, setQuizError] = useState('');
    const [failScore, setFailScore] = useState<number | null>(null);

    const [isPlaying, setIsPlaying] = useState(false);

    const playerRef = useRef<YTPlayer | null>(null);
    const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Load watch details
    useEffect(() => {
        if (!id) return;
        getWatchDetails(id).then(res => {
            if (!res.success || !res.data) {
                setErrorMsg(res.message ?? 'Badge not found.');
                setPageState('error');
                return;
            }
            setDetails(res.data);
            if (res.data.alreadyEarned) {
                setPageState('already-earned');
            } else if (res.data.videoUrl) {
                setPageState('watching');
            } else {
                setErrorMsg('This badge has no video attached.');
                setPageState('error');
            }
        }).catch(() => {
            setErrorMsg('Failed to load badge.');
            setPageState('error');
        });
    }, [id]);

    // Init YouTube player once in watching state
    useEffect(() => {
        if (pageState !== 'watching' || !details?.videoUrl) return;
        const videoId = extractVideoId(details.videoUrl);
        if (!videoId) {
            setErrorMsg('Could not extract YouTube video ID from URL.');
            setPageState('error');
            return;
        }

        const initPlayer = () => {
            playerRef.current = new window.YT.Player('yt-player', {
                videoId,
                // controls:0 hides the YouTube control bar (including the scrubber).
                // disablekb:1 blocks keyboard shortcuts so users can't seek with arrow keys.
                playerVars: { rel: 0, modestbranding: 1, autoplay: 0, controls: 0, disablekb: 1 },
                events: {
                    onReady: () => { setWatchStart(Date.now()); },
                    onStateChange: (event: { data: number }) => {
                        if (event.data === window.YT.PlayerState.PLAYING) {
                            setIsPlaying(true);
                            if (!progressIntervalRef.current) {
                                progressIntervalRef.current = setInterval(() => {
                                    const p = playerRef.current;
                                    if (p) {
                                        const dur = p.getDuration();
                                        const cur = p.getCurrentTime();
                                        if (dur > 0) setVideoProgress(Math.min(100, (cur / dur) * 100));
                                    }
                                }, 1000);
                            }
                        }
                        if (event.data === window.YT.PlayerState.PAUSED) {
                            setIsPlaying(false);
                        }
                        if (event.data === window.YT.PlayerState.ENDED) {
                            setIsPlaying(false);
                            if (progressIntervalRef.current) {
                                clearInterval(progressIntervalRef.current);
                                progressIntervalRef.current = null;
                            }
                            setVideoProgress(100);
                            if (details.hasQuiz && details.questions.length > 0) {
                                setPageState('quiz');
                            } else {
                                handleComplete();
                            }
                        }
                    },
                },
            });
        };

        if (window.YT && window.YT.Player) {
            initPlayer();
        } else {
            window.onYouTubeIframeAPIReady = initPlayer;
            if (!document.getElementById('yt-api-script')) {
                const script = document.createElement('script');
                script.id = 'yt-api-script';
                script.src = 'https://www.youtube.com/iframe_api';
                document.head.appendChild(script);
            }
        }

        return () => {
            if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
            playerRef.current?.destroy();
            playerRef.current = null;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageState, details]);

    const handleComplete = async (quizAnswers?: Array<{ questionId: string; answer: string }>) => {
        if (!id) return;
        setPageState('submitting');
        const watchSecs = watchStart ? Math.round((Date.now() - watchStart) / 1000) : undefined;
        const res = await completeWatch(id, { quizAnswers, watchDurationSeconds: watchSecs });
        if (res.success) {
            setPageState('success');
        } else if (res.quizFailed) {
            setFailScore(res.score ?? 0);
            setPageState('quiz-failed');
        } else {
            setErrorMsg(res.message ?? 'Failed to issue badge.');
            setPageState('error');
        }
    };

    const handleQuizSubmit = () => {
        if (!details) return;
        const unanswered = details.questions.filter(q => !answers[q.questionId]);
        if (unanswered.length > 0) {
            setQuizError('Please answer all questions before submitting.');
            return;
        }
        setQuizError('');
        const quizAnswers = Object.entries(answers).map(([questionId, answer]) => ({ questionId, answer }));
        handleComplete(quizAnswers);
    };

    // ── Render states ──────────────────────────────────────────────────────

    if (pageState === 'loading') {
        return (
            <Container maxWidth="md" sx={{ py: 6 }}>
                <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2, mb: 2 }} />
                <Skeleton width="60%" height={40} />
                <Skeleton width="80%" />
            </Container>
        );
    }

    if (pageState === 'error') {
        return (
            <Container maxWidth="md" sx={{ py: 6 }}>
                <Alert severity="error" sx={{ mb: 3 }}>{errorMsg}</Alert>
                <Button component={Link} to="/available-badges" color="secondary">
                    Back to Available Badges
                </Button>
            </Container>
        );
    }

    if (pageState === 'already-earned') {
        return (
            <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
                <CheckCircleIcon sx={{ fontSize: 72, color: 'success.main', mb: 2 }} />
                <Typography variant="h5" fontWeight={700} mb={1}>Already in your collection!</Typography>
                <Typography color="text.secondary" mb={3}>
                    You've already earned <strong>{details?.badgeName}</strong>.
                </Typography>
                {details?.badgeImage && (
                    <Box component="img" src={details.badgeImage} alt={details.badgeName}
                        sx={{ width: 120, height: 120, objectFit: 'contain', mb: 3 }} />
                )}
                <Button component={Link} to="/badges">Go to My Badges</Button>
            </Container>
        );
    }

    if (pageState === 'success') {
        return (
            <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
                <WorkspacePremiumIcon sx={{ fontSize: 72, color: 'accent.main', mb: 2 }} />
                <Typography variant="h4" fontWeight={700} mb={1}>Badge Earned!</Typography>
                <Typography color="text.secondary" mb={3}>
                    You've earned <strong>{details?.badgeName}</strong>. Congratulations!
                </Typography>
                {details?.badgeImage && (
                    <Box component="img" src={details.badgeImage} alt={details.badgeName}
                        sx={{ width: 140, height: 140, objectFit: 'contain', mb: 3, mx: 'auto', display: 'block' }} />
                )}
                <Button component={Link} to="/badges" size="large">
                    View My Badges
                </Button>
            </Container>
        );
    }

    if (pageState === 'quiz-failed') {
        return (
            <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
                <ErrorOutlineIcon sx={{ fontSize: 72, color: 'error.main', mb: 2 }} />
                <Typography variant="h5" fontWeight={700} mb={1}>Quiz Not Passed</Typography>
                <Typography color="text.secondary" mb={3}>
                    You scored {failScore}%. You need 70% to earn this badge.
                </Typography>
                <Box display="flex" gap={2} justifyContent="center">
                    <Button onClick={() => {
                        setAnswers({});
                        setFailScore(null);
                        setPageState('quiz');
                    }}>
                        Try Again
                    </Button>
                    <Button component={Link} to="/available-badges" variant="text">
                        Back to Badges
                    </Button>
                </Box>
            </Container>
        );
    }

    if (pageState === 'submitting') {
        return (
            <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
                <CircularProgress size={56} sx={{ mb: 3 }} />
                <Typography variant="h6">Issuing your badge…</Typography>
            </Container>
        );
    }

    if (pageState === 'quiz' && details) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Typography variant="h5" fontWeight={700} mb={1}>Quick Quiz</Typography>
                <Typography color="text.secondary" mb={3}>
                    Answer all questions correctly (70% to pass) to earn your badge.
                </Typography>
                {quizError && <Alert severity="error" sx={{ mb: 2 }}>{quizError}</Alert>}
                <Box display="flex" flexDirection="column" gap={3}>
                    {details.questions.map((q: WatchQuestion, idx: number) => (
                        <Card key={q.questionId} variant="outlined">
                            <CardContent>
                                <Typography fontWeight={600} mb={1.5}>
                                    {idx + 1}. {q.questionText}
                                </Typography>
                                <RadioGroup
                                    value={answers[q.questionId] ?? ''}
                                    onChange={(e) => setAnswers(prev => ({ ...prev, [q.questionId]: e.target.value }))}
                                >
                                    {q.options.map(opt => (
                                        <FormControlLabel
                                            key={opt.answer}
                                            value={opt.answer}
                                            control={<Radio size="small" />}
                                            label={opt.answer}
                                        />
                                    ))}
                                </RadioGroup>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
                <Box mt={3} display="flex" gap={2}>
                    <Button variant="contained" size="large" onClick={handleQuizSubmit}>
                        Submit Answers
                    </Button>
                    <Button component={Link} to="/available-badges" variant="text" color="inherit">
                        Cancel
                    </Button>
                </Box>
            </Container>
        );
    }

    // Default: watching state
    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            {/* Badge header */}
            <Box display="flex" alignItems="center" gap={2} mb={3}>
                {details?.badgeImage ? (
                    <Box component="img" src={details.badgeImage} alt={details?.badgeName}
                        sx={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 1 }} />
                ) : (
                    <PlayCircleOutlineIcon sx={{ fontSize: 56, color: 'accent.main' }} />
                )}
                <Box>
                    <Typography variant="h5" fontWeight={700}>{details?.badgeName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Watch the video below to earn this badge
                        {details?.hasQuiz ? ' — a short quiz follows' : ''}
                    </Typography>
                </Box>
            </Box>

            {/* YouTube player */}
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    paddingTop: '56.25%', // 16:9
                    bgcolor: '#000',
                    borderRadius: 2,
                    overflow: 'hidden',
                    mb: 2,
                }}
            >
                <Box
                    id="yt-player"
                    sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                />
                {/* Transparent overlay — blocks the YouTube scrubber/right-click, exposes play/pause only */}
                <Box
                    onClick={() => {
                        const p = playerRef.current;
                        if (!p) return;
                        if (isPlaying) p.pauseVideo(); else p.playVideo();
                    }}
                    sx={{
                        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                        cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                >
                    {!isPlaying && (
                        <Box sx={{
                            width: 64, height: 64, borderRadius: '50%',
                            bgcolor: 'rgba(0,0,0,0.6)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            pointerEvents: 'none',
                        }}>
                            <PlayCircleOutlineIcon sx={{ fontSize: 48, color: '#fff' }} />
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Progress bar */}
            <Box mb={1}>
                <LinearProgress
                    variant="determinate"
                    value={videoProgress}
                    sx={{ height: 6, borderRadius: 3, bgcolor: 'grey.200' }}
                />
                <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                    {videoProgress < 100
                        ? `${Math.round(videoProgress)}% watched — watch to the end to earn your badge`
                        : 'Video complete!'}
                </Typography>
            </Box>

            {/* Description */}
            {details?.badgeDescription && (
                <Box mt={3}>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        dangerouslySetInnerHTML={{ __html: details.badgeDescription }}
                    />
                </Box>
            )}
        </Container>
    );
}
